/**
 * Primitives d'interface — la boîte à outils dont tout le reste est fait.
 *
 * Conventions shadcn : un composant par rôle, des variantes nommées, et `cn()` pour que
 * l'appelant puisse toujours surcharger. Aucune couleur littérale ici — uniquement les
 * jetons sémantiques de `theme.css`, ce qui rend la bascule clair/sombre gratuite.
 */

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { ReactNode } from "react";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Seuils AIAG. 1,33 est la capabilité exigée en série, 1,00 la limite du tolérable. */
export const CAPABLE = 1.33;
export const ACCEPTABLE = 1.0;

export type Verdict = "good" | "warn" | "bad" | "unknown";

export function verdictOf(value: number | null | undefined): Verdict {
  if (value === null || value === undefined || Number.isNaN(value))
    return "unknown";
  if (value >= CAPABLE) return "good";
  if (value >= ACCEPTABLE) return "warn";
  return "bad";
}

export const VERDICT_LABEL: Record<Verdict, string> = {
  good: "Capable",
  warn: "À surveiller",
  bad: "Critique",
  unknown: "Indéterminé",
};

const VERDICT_CLASS: Record<Verdict, string> = {
  good: "text-good bg-good-soft",
  warn: "text-warn bg-warn-soft",
  bad: "text-bad bg-bad-soft",
  unknown: "text-faint bg-surface-2",
};

// --- Surfaces ---------------------------------------------------------------

export function Card({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-[var(--radius-card)] border border-line bg-surface",
        "shadow-[0_1px_2px_rgb(var(--c-shadow)/var(--shadow-strength))]",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  step,
  hint,
  right,
}: {
  title: ReactNode;
  step?: number;
  hint?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
      <div className="min-w-0">
        <h2 className="flex items-center gap-2 text-[0.9rem] font-semibold tracking-tight">
          {step !== undefined && (
            <span className="tnum inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[0.7rem] font-bold text-accent">
              {step}
            </span>
          )}
          {title}
        </h2>
        {hint && <p className="mt-0.5 text-[0.78rem] text-muted">{hint}</p>}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

// --- Contrôles --------------------------------------------------------------

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "outline";
  size?: "sm" | "md";
  active?: boolean;
};

export function Button({
  variant = "outline",
  size = "sm",
  active = false,
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-md font-medium",
        "transition-colors duration-150 outline-none",
        "focus-visible:ring-2 focus-visible:ring-accent/50",
        // Un bouton désactivé doit rester **lisible**. `opacity-50` était un raccourci
        // fautif : sur fond sombre, un aplat bleu à demi transparent vire au bleu-nuit
        // et son texte disparaît — on ne distingue plus « indisponible » de « cassé ».
        // Un état désactivé se signale par un contraste bas mais suffisant, pas par un
        // effacement.
        "disabled:pointer-events-none disabled:border disabled:border-line",
        "disabled:bg-surface-2 disabled:text-muted disabled:shadow-none",
        size === "sm" ? "h-7 px-2.5 text-[0.8rem]" : "h-9 px-4 text-[0.875rem]",
        variant === "primary" && "bg-accent text-accent-ink hover:opacity-90",
        variant === "outline" &&
          !active &&
          "border border-line bg-surface text-ink hover:bg-surface-2",
        variant === "outline" &&
          active &&
          "border border-accent bg-accent text-accent-ink",
        variant === "ghost" && "text-muted hover:bg-surface-2 hover:text-ink",
        className,
      )}
      {...rest}
    />
  );
}

export function Select({
  className,
  ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        "h-7 rounded-md border border-line bg-surface px-1.5 text-[0.8rem] text-ink",
        "outline-none transition-colors hover:border-line-strong",
        "focus-visible:ring-2 focus-visible:ring-accent/50",
        className,
      )}
      {...rest}
    />
  );
}

export function Input({
  className,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        "h-7 rounded-md border border-line bg-surface px-2 text-[0.8rem] text-ink",
        "outline-none transition-colors hover:border-line-strong",
        "focus-visible:ring-2 focus-visible:ring-accent/50",
        className,
      )}
      {...rest}
    />
  );
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[0.68rem] font-medium tracking-wide text-faint uppercase">
        {label}
      </span>
      {children}
    </label>
  );
}

// --- Signalétique -----------------------------------------------------------

export function Badge({
  tone = "neutral",
  className,
  children,
}: {
  tone?: Verdict | "neutral" | "accent";
  className?: string;
  children: ReactNode;
}) {
  const tones: Record<string, string> = {
    ...VERDICT_CLASS,
    neutral: "text-muted bg-surface-2",
    accent: "text-accent bg-accent-soft",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5",
        "text-[0.7rem] font-medium whitespace-nowrap",
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function VerdictDot({ verdict }: { verdict: Verdict }) {
  const colour: Record<Verdict, string> = {
    good: "bg-good",
    warn: "bg-warn",
    bad: "bg-bad",
    unknown: "bg-faint",
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn("h-1.5 w-1.5 shrink-0 rounded-full", colour[verdict])}
      />
      <span className="text-[0.78rem]">{VERDICT_LABEL[verdict]}</span>
    </span>
  );
}

/** Squelette de chargement. Une barre qui balaie vaut mieux qu'un texte « Chargement… » :
 *  elle occupe la place que le contenu prendra, donc l'écran ne saute pas à l'arrivée. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("relative overflow-hidden rounded bg-surface-2", className)}
    >
      <div className="absolute inset-y-0 -left-1/2 w-1/3 animate-[sweep_1.4s_ease-in-out_infinite] bg-linear-to-r from-transparent via-line to-transparent" />
    </div>
  );
}

// --- Tableau ----------------------------------------------------------------

export function Th({
  className,
  children,
  ...rest
}: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        "border-b border-line px-3 py-2 text-left",
        "text-[0.68rem] font-semibold tracking-wide text-faint uppercase",
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export function Td({
  className,
  children,
  ...rest
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      className={cn(
        "border-b border-line/60 px-3 py-1.5 align-middle",
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

/** Formatage français : espace insécable fine comme séparateur de milliers. */
export function num(value: number, digits = 0): string {
  return value.toLocaleString("fr-FR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function index(value: number | null | undefined): string {
  return value === null || value === undefined || Number.isNaN(value)
    ? "—"
    : value.toFixed(2);
}
